import type { BusRoute, MapVehicle } from '@/types'
import {
  aheadVehicleCheckpoint,
  collectRouteStationIds,
  deduplicateVehicleStateTargets,
  DISCOVERY_SLICE_COUNT,
  discoveryStationSlice,
  stableVehicleIdentity,
  type VehicleStateTarget,
} from '@/utils/vehiclePolling'

import { ByteReader, ByteWriter, decodeResponse, encodeRequest } from './timbusEnvelope'
import { fetchWorkerBatch, type WorkerBatchCommand, type WorkerBatchResult } from './workerApi'

const VEHICLE_REQUEST_TIMEOUT = 8_000

export interface VehicleStateRequest {
  stationId: number
  routeCode: string
  plate?: string
}

export function createVehicleStateRequest({
  stationId,
  routeCode,
  plate,
}: VehicleStateRequest): string {
  const writer = new ByteWriter()
  writer.int(stationId)
  writer.shortString(routeCode)
  writer.shortString(plate)
  writer.byte(0)
  return encodeRequest(writer.build())
}

/**
 * Decode every row in one VehicleState response. The upstream identifies the
 * observed route with `fleetCode`; only a single-route request may safely use
 * the requested route as a compatibility fallback when that field is absent.
 */
function parseVehicles(payload: Uint8Array, requestedRouteCodes: string[]): MapVehicle[] {
  if (payload.length === 0) return []
  const reader = new ByteReader(payload)
  const count = reader.unsignedByte()
  const vehicles: MapVehicle[] = []
  // A feed row normally has a unique plate, but the protocol does not promise
  // that (and duplicate plate/direction rows can occur while a bus is being
  // reassigned). Keep the familiar stable id for the common case, then suffix
  // only collisions so Vue list keys and exact-row navigation never collapse a
  // valid record into the preceding one.
  const idOccurrences = new Map<string, number>()

  for (let index = 0; index < count && reader.remaining() > 0; index += 1) {
    const plate = reader.string(1)
    const fleetCode = reader.string(1)
    const distanceToStop = reader.int()
    const secondsToStop = reader.int()
    const nextStop = reader.string(2)
    const speed = reader.signedByte()
    const lat = reader.float()
    const lng = reader.float()
    const directionValue = reader.unsignedByte()
    reader.signedByte()

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180 ||
      (lat === 0 && lng === 0)
    ) {
      continue
    }

    const direction =
      directionValue === 0 ? 'inbound' : directionValue === 1 ? 'outbound' : undefined
    if (!direction || (!plate && !fleetCode)) continue
    const routeCode = fleetCode || (requestedRouteCodes.length === 1 ? requestedRouteCodes[0] : '')
    // A combined request without fleetCode cannot be assigned to one route
    // without inventing identity, so leave that ambiguous row out.
    if (!routeCode) continue
    const label = plate || fleetCode || routeCode

    const baseId = `${fleetCode || routeCode}-${plate || index}-${direction}`
    const occurrence = idOccurrences.get(baseId) ?? 0
    idOccurrences.set(baseId, occurrence + 1)
    // Keep the collision suffix out of the hyphen-delimited route/plate/
    // direction shape. `plateFromVehicleId` deliberately parses the middle
    // segment for exact-bus fallback navigation, so a `-1` after direction
    // would be mistaken for part of the plate.
    const id = occurrence === 0 ? baseId : `${baseId}~${occurrence}`

    vehicles.push({
      id,
      label,
      plate: plate || undefined,
      fleetCode: fleetCode || undefined,
      routeCode,
      direction,
      lat,
      lng,
      ...(speed >= 0 ? { speed } : {}),
      ...(secondsToStop >= 0
        ? { minutesToNextStop: Math.ceil(secondsToStop / 60), secondsToStop }
        : {}),
      ...(nextStop ? { nextStop } : {}),
      source: 'live',
      ...(distanceToStop >= 0 ? { distanceToStop } : {}),
    })
  }

  return vehicles
}

interface VehicleStatePlan extends VehicleStateTarget {
  kind: 'discovery' | 'targeted' | 'stop'
  targetIdentity?: string
}

interface VehicleStateOutcome {
  plan: VehicleStatePlan
  vehicles?: MapVehicle[]
  error?: Error
}

interface FetchVehicleOptions {
  signal?: AbortSignal
  session?: string
}

function resultError(result: WorkerBatchResult): Error | undefined {
  if (result.error) return new Error('Timbus upstream is unavailable')
  if (result.status < 200 || result.status >= 300) {
    return new Error(`VehicleState returned HTTP ${result.status}`)
  }
  return undefined
}

function routeStationIdsAtStop(route: BusRoute, stopId: number): number[] {
  const stationIds = new Set<number>()
  for (const direction of ['outbound', 'inbound'] as const) {
    const stops = route.directions[direction].stops
    if (!stops.some((stop) => stop.id === stopId)) continue
    for (const stop of stops) stationIds.add(stop.id)
  }
  return [...stationIds]
}

function parseBatchResult(result: WorkerBatchResult, plan: VehicleStatePlan): VehicleStateOutcome {
  const error = resultError(result)
  if (error) return { plan, error }
  try {
    return {
      plan,
      vehicles: parseVehicles(decodeResponse(result.body), plan.routeCode.split(',')),
    }
  } catch (cause) {
    return {
      plan,
      error: cause instanceof Error ? cause : new Error('VehicleState returned invalid data'),
    }
  }
}

async function executeVehicleStatePlans(
  plans: VehicleStatePlan[],
  { signal, session }: FetchVehicleOptions,
): Promise<VehicleStateOutcome[]> {
  const uniquePlans = deduplicateVehicleStateTargets(plans)
  if (uniquePlans.length === 0) return []

  const commands: WorkerBatchCommand[] = uniquePlans.map((plan, index) => ({
    id: `vehicle-${index}`,
    command: 'VehicleState',
    requestParameter: createVehicleStateRequest(plan),
  }))
  const signals = [AbortSignal.timeout(VEHICLE_REQUEST_TIMEOUT)]
  if (signal) signals.push(signal)
  const results = await fetchWorkerBatch(commands, {
    session,
    signal: AbortSignal.any(signals),
  })
  return results.map((result, index) => parseBatchResult(result, uniquePlans[index]!))
}

interface TrackedVehicle {
  vehicle: MapVehicle
  lastObservedHorizon: number
}

function observeVehicles(
  tracked: Map<string, TrackedVehicle>,
  vehicles: MapVehicle[],
  horizon: number,
) {
  for (const vehicle of vehicles) {
    tracked.set(stableVehicleIdentity(vehicle), {
      vehicle: { ...vehicle, source: 'live' },
      lastObservedHorizon: horizon,
    })
  }
}

function copyTracked(tracked: Map<string, TrackedVehicle>): Map<string, TrackedVehicle> {
  return new Map(
    [...tracked].map(([identity, state]) => [
      identity,
      { ...state, vehicle: { ...state.vehicle } },
    ]),
  )
}

function markLastKnown(tracked: Map<string, TrackedVehicle>, identity: string) {
  const state = tracked.get(identity)
  if (state) state.vehicle = { ...state.vehicle, source: 'last-known' }
}

export interface RouteVehiclePoller {
  refresh(options?: FetchVehicleOptions): Promise<MapVehicle[]>
}

export interface StopFleetRefresh {
  stopVehicles: MapVehicle[]
  /** Successful route-slice observations, keyed by route id. */
  routeVehicles: Map<number, MapVehicle[]>
}

/**
 * One poller belongs to one route query instance. It completes a strict initial
 * sweep, then keeps exact plated refreshes and discovery-horizon state private
 * to that query rather than leaking it into the app-wide query cache.
 */
export function createRouteVehiclePoller(route: BusRoute): RouteVehiclePoller {
  const stationIds = collectRouteStationIds(route)
  let tracked = new Map<string, TrackedVehicle>()
  let initialized = false
  let cycle = 0
  let horizon = 1
  const successfulDiscoverySlices = new Set<number>()

  async function initialRefresh(options: FetchVehicleOptions): Promise<MapVehicle[]> {
    const plans: VehicleStatePlan[] = stationIds.map((stationId) => ({
      kind: 'discovery',
      stationId,
      routeCode: route.code,
    }))
    const outcomes = await executeVehicleStatePlans(plans, options)
    const failure = outcomes.find((outcome) => outcome.error)?.error
    if (failure) throw failure

    const initial = new Map<string, TrackedVehicle>()
    for (const outcome of outcomes) observeVehicles(initial, outcome.vehicles ?? [], 0)
    tracked = initial
    initialized = true
    return [...tracked.values()].map(({ vehicle }) => vehicle)
  }

  async function steadyRefresh(options: FetchVehicleOptions): Promise<MapVehicle[]> {
    const slice = cycle % DISCOVERY_SLICE_COUNT
    const plans: VehicleStatePlan[] = discoveryStationSlice(stationIds, slice).map((stationId) => ({
      kind: 'discovery',
      stationId,
      routeCode: route.code,
    }))

    for (const [identity, state] of tracked) {
      if (!state.vehicle.plate) continue
      const checkpoint = aheadVehicleCheckpoint(route, state.vehicle)
      if (!checkpoint) continue
      plans.push({
        kind: 'targeted',
        stationId: checkpoint.id,
        routeCode: route.code,
        plate: state.vehicle.plate,
        targetIdentity: identity,
      })
    }

    const outcomes = await executeVehicleStatePlans(plans, options)
    const successes = outcomes.filter((outcome) => outcome.vehicles !== undefined)
    if (outcomes.length > 0 && successes.length === 0) {
      throw outcomes[0]!.error ?? new Error('VehicleState refresh failed')
    }

    const next = copyTracked(tracked)
    for (const [identity, state] of next) {
      if (!state.vehicle.plate) markLastKnown(next, identity)
    }

    for (const outcome of outcomes) {
      if (outcome.plan.kind === 'targeted' && outcome.plan.targetIdentity) {
        // Both an ordinary failure and a successful exact-filter miss mean the
        // previous coordinate is no longer confirmed live.
        markLastKnown(next, outcome.plan.targetIdentity)
      }
    }
    // Discovery and targeted commands can observe the same vehicle in one
    // refresh. Apply every successful observation after misses/failures so fresh
    // data wins regardless of command order.
    for (const outcome of outcomes) {
      if (outcome.vehicles) observeVehicles(next, outcome.vehicles, horizon)
    }

    const discoveryOutcomes = outcomes.filter((outcome) => outcome.plan.kind === 'discovery')
    if (discoveryOutcomes.every((outcome) => outcome.vehicles !== undefined)) {
      successfulDiscoverySlices.add(slice)
    }

    if (slice === DISCOVERY_SLICE_COUNT - 1) {
      if (successfulDiscoverySlices.size === DISCOVERY_SLICE_COUNT) {
        for (const [identity, state] of next) {
          if (state.lastObservedHorizon < horizon) next.delete(identity)
        }
      }
      successfulDiscoverySlices.clear()
      horizon += 1
    }

    tracked = next
    cycle += 1
    return [...tracked.values()].map(({ vehicle }) => vehicle)
  }

  return {
    refresh(options = {}) {
      return initialized ? steadyRefresh(options) : initialRefresh(options)
    },
  }
}

/**
 * Compatibility helper for callers that need a one-off complete route sweep.
 * Live query callers retain the poller returned by createRouteVehiclePoller.
 */
export function fetchRouteVehicles(
  route: BusRoute,
  options: FetchVehicleOptions = {},
): Promise<MapVehicle[]> {
  return createRouteVehiclePoller(route).refresh(options)
}

export interface StopFleetRefreshOptions extends FetchVehicleOptions {
  /** One deterministic discovery slice for routes not already in query cache. */
  routeSlice?: { routes: BusRoute[]; slice: number }
}

/**
 * Refresh the selected stop and, when requested, one discovery slice for every
 * route whose complete fleet is not cached yet. All plans share one Worker
 * batch call (the normal 50-command chunking still applies), so the stop row
 * never creates one browser request per route. The stop command is atomic;
 * route-slice failures are deliberately partial because the next five-second
 * tick retries the next deterministic slice while preserving prior rows.
 */
export async function fetchStopFleetRefresh(
  stopId: number,
  routeCodes: string[],
  options: StopFleetRefreshOptions = {},
): Promise<StopFleetRefresh> {
  const uniqueRouteCodes = [...new Set(routeCodes)]
  if (uniqueRouteCodes.length === 0) return { stopVehicles: [], routeVehicles: new Map() }

  const plans: VehicleStatePlan[] = []
  const routeByCode = new Map<string, BusRoute>()
  const routeSlice = options.routeSlice
  if (routeSlice) {
    for (const route of routeSlice.routes) {
      if (routeByCode.has(route.code) || !uniqueRouteCodes.includes(route.code)) continue
      routeByCode.set(route.code, route)
      // The combined stop command below already refreshes this station with
      // the complete route list. Excluding it from the directional hydration
      // slice avoids a second upstream request for the same station while
      // keeping the six-slice route fleet coverage deterministic.
      const hydrationStationIds = routeStationIdsAtStop(route, stopId).filter(
        (stationId) => stationId !== stopId,
      )
      for (const stationId of discoveryStationSlice(hydrationStationIds, routeSlice.slice)) {
        plans.push({ kind: 'discovery', stationId, routeCode: route.code })
      }
    }
  }
  // Append the atomic stop plan after route hydration so its exact stop rows
  // are applied after any broader route observations in the returned merge.
  plans.push({ kind: 'stop', stationId: stopId, routeCode: uniqueRouteCodes.join(',') })

  const outcomes = await executeVehicleStatePlans(plans, options)
  const stopOutcome = outcomes.find((outcome) => outcome.plan.kind === 'stop')
  if (!stopOutcome || stopOutcome.vehicles === undefined) {
    throw stopOutcome?.error ?? new Error('VehicleState is unreachable for this stop')
  }

  const routeVehicles = new Map<number, MapVehicle[]>()
  for (const outcome of outcomes) {
    if (outcome.plan.kind !== 'discovery' || !outcome.vehicles) continue
    const route = routeByCode.get(outcome.plan.routeCode)
    if (!route) continue
    const existing = routeVehicles.get(route.id)
    if (existing) existing.push(...outcome.vehicles)
    else routeVehicles.set(route.id, [...outcome.vehicles])
  }

  return { stopVehicles: sortByArrival(stopOutcome.vehicles), routeVehicles }
}

/** Buses approaching one stop, using the one-command station-only path. */
export async function fetchStopVehicles(
  stopId: number,
  routeCodes: string[],
  options: FetchVehicleOptions = {},
): Promise<MapVehicle[]> {
  return (await fetchStopFleetRefresh(stopId, routeCodes, options)).stopVehicles
}

/**
 * Soonest first. Vehicles with no reported ETA sort last rather than being
 * dropped because the feed also includes buses not yet committed to the stop.
 */
export function sortByArrival(vehicles: MapVehicle[]): MapVehicle[] {
  return [...vehicles].sort((a, b) => {
    const left = a.secondsToStop ?? Number.POSITIVE_INFINITY
    const right = b.secondsToStop ?? Number.POSITIVE_INFINITY
    return left - right
  })
}

export function collectDiscoveryStationIds(route: BusRoute): number[] {
  return collectRouteStationIds(route)
}
